/**
 * Task #245 — single row of the Spotlight quick-search palette.
 *
 *   [thumb] [P-ID badge] [Project Name (truncated, highlighted)]    ⌘↵
 *           └─ [via project name] [via client "X"] [via year 2026]
 *
 * Phase 3 — when multi-token search produces hits across multiple
 * fields (e.g. token1 → clientName via chained narrowing, token2 →
 * projectNumber), each field is rendered as its own chip on the
 * secondary line so the user can see exactly which tokens hit where.
 * Single-token / single-field rows keep their compact "Matched via X"
 * rendering.
 */

import { Tag, Hash, Building2, Calendar, User, Pin } from 'lucide-react';
import { IconTooltip } from '@/components/ui/icon-tooltip';
import { CommandItem } from '@/components/ui/command';
import { HighlightedText } from './HighlightedText';
import { visibleSlice } from '@/lib/quick-search/ranking';
import type { MatchReason, MatchReasonField, SearchHit } from '@/lib/quick-search/types';

interface Props {
  hit: SearchHit;
  onSelect: (hit: SearchHit) => void;
  onPin: (hit: SearchHit) => void;
}

const FIELD_LABEL: Record<MatchReasonField, string> = {
  projectName: 'project name',
  projectNumber: 'project ID',
  clientName: 'client',
  keyword: 'keyword',
  year: 'year',
  leadArtist: 'Lead artist',
  artist: 'Artist',
};

function ReasonIcon({ field }: { field: MatchReasonField }) {
  const cls = 'h-3 w-3 shrink-0';
  switch (field) {
    case 'keyword':
      return <Tag className={cls} />;
    case 'clientName':
      return <Building2 className={cls} />;
    case 'year':
      return <Calendar className={cls} />;
    case 'leadArtist':
    case 'artist':
      return <User className={cls} />;
    default:
      return <Hash className={cls} />;
  }
}

function ReasonChip({ reason }: { reason: MatchReason }) {
  const isProjectName = reason.field === 'projectName';
  return (
    <span className="inline-flex items-center gap-1 rounded bg-muted/40 px-1.5 py-0.5">
      <ReasonIcon field={reason.field} />
      {isProjectName ? (
        <span>via project name</span>
      ) : (
        <>
          <span>via {FIELD_LABEL[reason.field]}:</span>
          <HighlightedText
            text={reason.value}
            indices={reason.matchIndices}
            className="truncate"
          />
        </>
      )}
    </span>
  );
}

export function QuickSearchResultRow({ hit, onSelect, onPin }: Props) {
  const projectNameMatched = hit.matchReason.field === 'projectName';
  // For project-name matches the matchReason.value already mirrors
  // projectName, and may carry multiple match indices (Phase 3 merges
  // same-field highlights). Use that value+indices so every token's hit
  // on the project name lights up in one pass.
  const nameForRender = projectNameMatched ? hit.matchReason.value : hit.projectName;
  const nameIndices = projectNameMatched ? hit.matchReason.matchIndices : [];
  const sliced = visibleSlice(nameForRender, nameIndices, 60);

  // Phase 3 — render up to two chips on the secondary line. Cap exists
  // so a 4-token query doesn't blow out the row width on mobile.
  const additional = (hit.additionalMatches ?? []).slice(0, 2);
  const hasAdditional = additional.length > 0;

  return (
    <CommandItem
      key={hit.id}
      value={`${hit.projectId} ${hit.projectName} ${hit.clientName} ${hit.matchReason.value}`}
      onSelect={() => onSelect(hit)}
      data-testid={`quick-search-row-${hit.projectId}`}
      className="flex flex-col items-stretch gap-0.5 px-2 py-2"
    >
      <div className="flex items-center gap-2">
        {hit.thumbnailUrl ? (
          <img
            src={hit.thumbnailUrl}
            alt=""
            loading="lazy"
            className="w-[76px] h-[37.5px] shrink-0 rounded object-cover"
            data-testid={`quick-search-thumb-${hit.projectId}`}
          />
        ) : (
          <div
            className="w-[76px] h-[37.5px] shrink-0 rounded bg-muted/60"
            aria-hidden="true"
            data-testid={`quick-search-thumb-placeholder-${hit.projectId}`}
          />
        )}
        <span
          className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/80"
          data-testid={`quick-search-id-${hit.projectId}`}
        >
          {hit.projectId}
        </span>
        <span
          className="flex-1 min-w-0 truncate text-sm"
          title={hit.projectName}
          data-testid={`quick-search-name-${hit.projectId}`}
        >
          <HighlightedText text={sliced.text} indices={sliced.indices} />
        </span>
        <kbd className="ml-auto shrink-0 text-[10px] tracking-widest text-muted-foreground">
          ⌘↵
        </kbd>
        <IconTooltip label="Pin & focus this project">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onPin(hit);
            }}
            onPointerDown={(e) => {
              // cmdk listens on pointerdown to select the row — stop it
              // so clicking the pin button doesn't also fire navigate.
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            aria-label={`Pin ${hit.projectName} and show only this project`}
            title="Pin & focus this project"
            data-testid={`quick-search-pin-${hit.projectId}`}
            className="shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-md border border-border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <Pin className="h-3.5 w-3.5" />
          </button>
        </IconTooltip>
      </div>
      <div
        className="ml-9 flex flex-wrap items-center gap-1 text-[11px] text-foreground/80"
        data-testid={`quick-search-reason-${hit.projectId}`}
      >
        <span className="opacity-60">└─</span>
        {hasAdditional ? (
          <>
            <ReasonChip reason={hit.matchReason} />
            {additional.map((r, i) => (
              <ReasonChip key={`${r.field}-${i}`} reason={r} />
            ))}
          </>
        ) : projectNameMatched ? (
          <>
            <ReasonIcon field={hit.matchReason.field} />
            <span>Matched via project name</span>
          </>
        ) : (
          <>
            <ReasonIcon field={hit.matchReason.field} />
            <span>Matched via {FIELD_LABEL[hit.matchReason.field]}: </span>
            <HighlightedText
              text={hit.matchReason.value}
              indices={hit.matchReason.matchIndices}
              className="truncate"
            />
          </>
        )}
      </div>
    </CommandItem>
  );
}
